import { Controller } from "@hotwired/stimulus"

const PASSWORD_RULES = [
  {
    key: "length",
    message: "At least 12 characters",
    test: (password) => password.length >= 12
  },
  {
    key: "uppercase",
    message: "At least 1 uppercase letter",
    test: (password) => /[A-Z]/.test(password)
  },
  {
    key: "lowercase",
    message: "At least 1 lowercase letter",
    test: (password) => /[a-z]/.test(password)
  },
  {
    key: "digit",
    message: "At least 1 number",
    test: (password) => /[0-9]/.test(password)
  },
  {
    key: "special",
    message: "At least 1 special character (#?!@$%^&*-)",
    test: (password) => /[#?!@$%^&*-]/.test(password)
  }
]

export default class extends Controller {
  static targets = [
    "password",
    "passwordConfirmation",
    "requirement",
    "confirmationMessage",
    "formError",
    "submit"
  ]

  static values = {
    optional: { type: Boolean, default: false }
  }

  connect() {
    this.refreshValidation()
  }

  validatePassword() {
    this.refreshValidation()
  }

  validateConfirmation() {
    this.refreshValidation()
  }

  validateSubmission(event) {
    if (!this.refreshValidation({ showErrors: true })) {
      event.preventDefault()
    }
  }

  refreshValidation({ showErrors = false } = {}) {
    const password = this.passwordTarget.value || ""
    const confirmation = this.passwordConfirmationTarget.value || ""
    const hasPasswordInput = password.length > 0
    const bothEmpty = !hasPasswordInput && confirmation.length === 0

    if (this.optionalValue && bothEmpty) {
      this.requirementTargets.forEach((el) => this.applyNeutralStyle(el))
      this.confirmationMessageTarget.textContent = ""
      this.applyNeutralStyle(this.confirmationMessageTarget)
      this.passwordTarget.removeAttribute("aria-invalid")
      this.passwordConfirmationTarget.removeAttribute("aria-invalid")
      this.formErrorTarget.textContent = ""
      this.formErrorTarget.classList.add("hidden")
      this.setSubmitState(true)
      return true
    }

    const unmetRequirements = []

    this.requirementTargets.forEach((requirementElement) => {
      const ruleKey = requirementElement.dataset.passwordValidationRuleValue
      const rule = PASSWORD_RULES.find((candidate) => candidate.key === ruleKey)

      if (!rule) return

      const rulePasses = rule.test(password)

      if (!rulePasses) {
        unmetRequirements.push(rule.message)
      }

      if (!hasPasswordInput) {
        this.applyNeutralStyle(requirementElement)
      } else if (rulePasses) {
        this.applyValidStyle(requirementElement)
      } else {
        this.applyInvalidStyle(requirementElement)
      }
    })

    const matches = confirmation.length > 0 && confirmation === password
    const confirmationIsInvalid = confirmation.length > 0 && !matches

    if (confirmation.length === 0) {
      this.confirmationMessageTarget.textContent = ""
      this.applyNeutralStyle(this.confirmationMessageTarget)
    } else if (matches) {
      this.confirmationMessageTarget.textContent = "Passwords match."
      this.applyValidStyle(this.confirmationMessageTarget)
    } else {
      this.confirmationMessageTarget.textContent = "Passwords do not match."
      this.applyInvalidStyle(this.confirmationMessageTarget)
    }

    const passwordIsValid = hasPasswordInput && unmetRequirements.length === 0
    const formIsValid = passwordIsValid && matches

    this.passwordTarget.setAttribute("aria-invalid", (hasPasswordInput && !passwordIsValid).toString())
    this.passwordConfirmationTarget.setAttribute("aria-invalid", confirmationIsInvalid.toString())
    this.setSubmitState(formIsValid)

    if (showErrors && !formIsValid) {
      const errorMessages = []

      if (!passwordIsValid) {
        errorMessages.push("Password must meet all listed requirements.")
      }

      if (!matches) {
        errorMessages.push("Password confirmation must match.")
      }

      this.formErrorTarget.textContent = errorMessages.join(" ")
      this.formErrorTarget.classList.remove("hidden")
    } else {
      this.formErrorTarget.textContent = ""
      this.formErrorTarget.classList.add("hidden")
    }

    return formIsValid
  }

  setSubmitState(isValid) {
    this.submitTarget.disabled = !isValid
    this.submitTarget.classList.toggle("opacity-50", !isValid)
    this.submitTarget.classList.toggle("cursor-not-allowed", !isValid)
    this.submitTarget.classList.toggle("cursor-pointer", isValid)
  }

  applyNeutralStyle(element) {
    element.classList.remove("text-green-700", "text-red-600")
    element.classList.add("text-gray-500")
  }

  applyValidStyle(element) {
    element.classList.remove("text-gray-500", "text-red-600")
    element.classList.add("text-green-700")
  }

  applyInvalidStyle(element) {
    element.classList.remove("text-gray-500", "text-green-700")
    element.classList.add("text-red-600")
  }
}
